/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaStringWithConstModel,
	isSchemaStringWithConstModel,
	SCHEMA_VALUE_TYPE
} from "@adapptio/lib-blueprint";
import { INSPECTOR_NODE_TYPE, isInspectorNodeDefault, TInspectorNode } from "../InspectorNodeTypes";
import { INSPECTOR_CONTROL_TYPE, TInspectorControl } from "../controls";
import { mapModelToNodes } from "../index";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";
import { getParentComponentName } from "../utils";

function getStringWithConstControl(model: ISchemaStringWithConstModel): TInspectorControl | null {
	switch (model.schema?.opts.editorOptions?.controlType) {
		case "colorSelector":
			return {
				key: String(model.nodeId),
				type: INSPECTOR_CONTROL_TYPE.COLOR,
				model
			};
		case "fontSizeSelector":
			return {
				key: String(model.nodeId),
				type: INSPECTOR_CONTROL_TYPE.FONT_SIZE,
				model
			};

		default:
			return {
				key: String(model.nodeId),
				type: INSPECTOR_CONTROL_TYPE.SELECT,
				model
			};
	}
}

export function stringWithConstMapper(model: ISchemaStringWithConstModel): TInspectorNode {
	if (!isSchemaStringWithConstModel(model)) {
		return;
	}
	const control = getStringWithConstControl(model);
	const valueInspectorNode = mapModelToNodes(model.value);

	const controls = [];

	if (model.value.type === SCHEMA_VALUE_TYPE.CONST) {
		if (control !== null) {
			controls.push(control);
		}

		if (isInspectorNodeDefault(valueInspectorNode)) {
			controls.push(...valueInspectorNode.controls.slice(-1));
		}
	} else {
		if (isInspectorNodeDefault(valueInspectorNode)) {
			controls.push(...valueInspectorNode.controls);
		}
	}

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		options: model.schema.opts["options"],
		required: model.schema.opts.constraints?.required || false,
		default: model.schema.opts.default || null,
		predefineValues: model.schema.opts.constants.map((el) => el.value),
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: [ model, ...valueInspectorNode.deps ],
		controls: controls,
		children: []
	};
}
